import countries from '../json/countries.json';
import { DOM } from '../helpers/DOM.js';

export class PhoneNumberField {
	#phoneCodesOptions = {};
	gamification = null;

	constructor() {
		setTimeout( async () => {
			await this.#initPhoneCodes();
			this.gamification = document.querySelector('input[name="gamification_check"]');
			await this.#initInputMask();
		}, 1 );
	}

	#normalizeCountryCode( countryCode ) {
		const code = String( countryCode || '' ).trim().toUpperCase();
		return countries.some( ( country ) => country.code === code ) ? code : '';
	}

	async #detectCountryCode() {
		try {
			const response = await fetch( '/wp-json/maxmind/v1/geoIP' );
			const detected = await response.json();
			const countryCode = this.#normalizeCountryCode( detected );

			if ( countryCode ) {
				sessionStorage.setItem(
					'levcharity_user_country_code',
					countryCode
				);
			}

			return countryCode;
		} catch ( error ) {
			return '';
		}
	}

	async #initPhoneCodes() {
		const selects = document.querySelectorAll( '.country-phone-codes' );

		if ( ! selects ) {
			return;
		}

		for ( const select of selects ) {
			const codeField = DOM.el( '.country-phone-code-input', select );
			const fallbackCountryCode =
				this.#normalizeCountryCode( select.dataset.defaultCountry ) || 'US';

			let selectedValue = this.#normalizeCountryCode(
				sessionStorage.getItem( 'levcharity_user_country_code' )
			);

			if ( ! selectedValue ) {
				selectedValue = await this.#detectCountryCode();
			}

			selectedValue = selectedValue || fallbackCountryCode;

			if ( codeField?.value ) {
				const value = '+' + codeField.value.replace( /\+|(\s+)/g, '' );
				const matching = countries.filter(
					( country ) => value === country?.dial_code
				);

				// Several countries share a dial code (e.g. +1 for US and CA),
				// so keep the detected country when it matches the stored code.
				if (
					matching.length &&
					! matching.some(
						( country ) => country.code === selectedValue
					)
				) {
					selectedValue = matching[ 0 ].code;
				}
			}

			await this.#loadPhoneCodes( select, selectedValue, '', true, true, codeField?.id || codeField.name );
		}

		DOM.addEvent( 'click', '.country-phone-codes', ( event, select ) => {
			event.preventDefault();
			const value = select.querySelector( '.value' );
			const input = select.querySelector( '.country-phone-code-input' );
			const item = event.target.matches( '.dropdown .item' )
				? event.target
				: event.target.closest( '.dropdown .item' );

			if ( ! select.classList.contains( 'is-open' ) ) {
				select.classList.add( 'is-open' );
				setTimeout( () => {
					select
						.querySelector( '.search-country-dial-code' )
						?.focus();
				}, 10 );
			} else if ( item && ! item.classList.contains( 'current' ) ) {
				select.classList.remove( 'is-open' );

				value.innerHTML = item.outerHTML;
				input.value = item?.dataset?.value || '';

				select.dataset.current = item.dataset.code ?? '';


				this?.onChange( item, select, input, value );
			}

			if ( item ) {
				item.classList.add( 'current' );
			}

			select
				.querySelectorAll( '.dropdown .item.current' )
				?.forEach( ( el ) => {
					if ( item && el !== item ) {
						el.classList.remove( 'current' );
					}
				} );
		} );

		DOM.addEvent( 'click', document, ( event, doc ) => {
			if ( event.target.matches( '.country-phone-codes *' ) ) {
				return;
			}

			if ( document.querySelectorAll( '.country-phone-codes.is-open' ) ) {
				document
					.querySelectorAll( '.country-phone-codes.is-open' )
					.forEach( ( el ) => {
						el.classList.remove( 'is-open' );
					} );
			}
		} );

		DOM.observeElements(
			'.country-phone-codes .item img.country-flag',
			( image ) => {
				if ( image?.dataset?.src ) {
					image.src = image.dataset.src;
					image.removeAttribute( 'data-src' );
				}
			}
		);

		DOM.addEvent(
			'input',
			'.search-country-dial-code',
			async ( event, field ) => {
				const select = field.closest( '.country-phone-codes' );
				const codeField = DOM.el( '.country-phone-code-input', select );
				await this.#loadPhoneCodes( select, null, field.value, false, false, codeField?.id || codeField.name);
			}
		);
	}

	async #getNumberLength(countryCode)  {
		// Dynamically import libphonenumber-js only when needed
		const { getExampleNumber } = await import(/* webpackChunkName: "libphonenumber" */ "libphonenumber-js");
		const { default: examples } = await import(/* webpackChunkName: "libphonenumber" */ 'libphonenumber-js/mobile/examples');
		const number = getExampleNumber(countryCode, examples);
		return number;
	}

	async #loadPhoneCodes(select, selectedValue = null, searchValue = null, lazy = true, firstTime = false, id = '') {
		const api_url = 'https://flagcdn.com/w40/';

		const emptySrc =
			'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAQSURBVHgBAQUA+v8AAAAAAAAFAAFkeJU4AAAAAElFTkSuQmCC';

		let value = null;
		let inputValue = '';
		const list = select.querySelector( '.dropdown .list' );
		list.innerHTML = '';

		const sortedCountries = [ ...countries ];
		if ( searchValue ) {
			sortedCountries.sort( ( a, b ) => {
				if (
					this.#countryInResults( a, searchValue ) ||
					this.#countryInResults( b, searchValue )
				) {
					if ( a.code.toLowerCase() === searchValue.toLowerCase() ) {
						return 1;
					}
					if ( b.code.toLowerCase() === searchValue.toLowerCase() ) {
						return -1;
					}

					const nameComparison = a.name.localeCompare( b.name );
					if ( nameComparison !== 0 ) {
						return -1 * nameComparison;
					}

					const dialCodeA = parseInt(
						a.dial_code.replace( '+', '' ),
						10
					);
					const dialCodeB = parseInt(
						b.dial_code.replace( '+', '' ),
						10
					);
					return dialCodeA - dialCodeB;
				}
				return this.#countryInResults( b, searchValue ) ? 1 : -1;
			} );
		}

		for ( const country of sortedCountries ) {
			const code = country.dial_code;
			const countryCode = country.code;
			const name = country.name;
			const numberLengthData = await this.#getNumberLength(countryCode);
			const numberLength = numberLengthData?.nationalNumber.length;

			if ( countryCode === 'PS' ) {
				continue;
			}

			let hide = false;

			if ( searchValue ) {
				if ( ! this.#countryInResults( country, searchValue ) ) {
					hide = true;
				}
			}

			let option =
				[].filter.call( this.#phoneCodesOptions[id] || [], function ( element ) {
					return element.matches( `.country-${ countryCode }` );
				} )[ 0 ] || null;

			if ( ! option ) {
				option = document.createElement( 'div' );
				option.className = `item${
					! value && countryCode === selectedValue ? ' current' : ''
				} country-${ countryCode }`;
				option.dataset.value = code.replace( '+', '' );
				option.dataset.code = countryCode;
				option.dataset.numberLength = String(numberLength);
				option.innerHTML = `<img class="country-flag" ${
					lazy ? `src="${ emptySrc }" data-` : ''
				}src="${ api_url }${ countryCode.toLowerCase() }.png" title="${ countryCode }"/><span class="code">${ code }</span><span class="name">${ name }</span>`;
			}

			if ( firstTime && countryCode === selectedValue ) {
				this?.onInit( option, select, selectedValue );
			}

			if ( hide ) {
				option.style.display = 'none';
			} else {
				option.style.display = '';
			}

			if ( countryCode === select?.dataset?.current ) {
				option.classList.add( 'current' );
			} else if (
				option.classList.contains( 'current' ) &&
				countryCode !== selectedValue
			) {
				option.classList.remove( 'current' );
			}

			if ( hide || ! searchValue ) {
				list.append( option );
			} else {
				list.prepend( option );
			}

			if ( selectedValue ) {
				if ( ! value && countryCode === selectedValue ) {
					inputValue = code.replace( '+', '' );
					value = option.cloneNode( true );
					value.classList.remove();
				}
			}
		}

		if ( selectedValue ) {
			select.querySelector( '.country-phone-code-input' ).value =
				inputValue;
			select.querySelector( '.value' ).innerHTML = '';
			select.querySelector( '.value' ).append( value );
			select.dataset.current = selectedValue;
		}

		if ( ! searchValue ) {
			this.#phoneCodesOptions[id] = [ ...list.children ];
		}
	}

	#countryInResults( country, searchValue ) {
		const searchArray = [
			country.code,
			country.name,
			country.capital,
			country.dial_code,
			country.emoji,
			country.unicode,
			...country.timezones,
		];

		return searchArray.some( ( value ) => {
			if ( ! value ) {
				return false;
			}

			const v = value
				.toLowerCase()
				.trim()
				.split( /[\s_\\\/]+/ )
				.join( '-' );
			const s = searchValue
				.toLowerCase()
				.trim()
				.split( /[\s_\\\/]+/ )
				.join( '-' );

			return (
				value === searchValue ||
				v === s ||
				v.startsWith( s ) ||
				v.includes( s )
			);
		} );
	}


	async #initInputMask() {
		const { default: Inputmask } = await import(/* webpackChunkName: "inputmask" */ '../../../node_modules/inputmask/lib/inputmask.js');


		const countrySelect = DOM.el('.country-phone-codes');
		const phoneInput = document.querySelector(".phone-number-field");
		if(!countrySelect || !phoneInput) return

		let currentMask = null;

		const updateMask = () => {
			const selectedOptionNew =
				countrySelect.querySelector(`.item.country-${ countrySelect.dataset.current }`) ||
				countrySelect.querySelector('.item.current') ||
				countrySelect.querySelector('.item');
			if ( ! selectedOptionNew ) {
				return;
			}
			const numberLength = selectedOptionNew.getAttribute('data-number-length');
			const countryCode = selectedOptionNew.getAttribute('data-value');

			if (phoneInput) {
				let mask = `(${countryCode})`;
				let placeholder = `(${countryCode})`;

				for (let i = 0; i < numberLength; i++) {
					mask += "9";
					placeholder += "_";
				}

				const fullNumberLength = Number(numberLength) + countryCode.length;

				const currentValue = phoneInput.value;

				phoneInput.setAttribute('placeholder', placeholder);
				phoneInput.setAttribute('data-full-number-length', String(fullNumberLength));

				if (currentMask) {
					currentMask.remove();
				}

				currentMask = new Inputmask(mask, {
					clearIncomplete: false,
					showMaskOnHover: false,
					showMaskOnFocus: false,
					insertMode: false,
					autoUnmask: false,
					rightAlign: false,
					positionCaretOnClick: "none",
					positionCaretOnTab: "none"
				});

				currentMask.mask(phoneInput);

				if (currentValue) {
					phoneInput.value = currentValue;
				}
			}
		};

		updateMask();

		countrySelect.addEventListener('click', function () {
			setTimeout(() => {
				updateMask();
			}, 500);
		});

		phoneInput.addEventListener('input', function(e) {
			const cursorPos = this.selectionStart;

			requestAnimationFrame(() => {
				if (cursorPos !== null && cursorPos <= this.value.length) {
					this.setSelectionRange(cursorPos, cursorPos);
				}
			});
		});
	}






	onChange() {}

	onInit() {}
}
